import React, { useState, useEffect } from 'react';
import sharepointService from '../services/sharepointService';
import CreateFolderModal from './CreateFolderModal';
import { validateFilename } from '../utils/filenameValidation';

/**
 * Modal component for relocating (renaming/moving) a file that's already in SharePoint.
 * This allows users to change the filename and/or move the file to a different folder
 * after it has already been approved and pushed to SharePoint.
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Function to close the modal
 * @param {object} file - The file object to relocate
 * @param {string} clientName - The client name
 * @param {string} knowledgebaseClientId - The knowledgebase client ID
 * @param {function} onRelocate - Callback function when file is relocated successfully
 */
export default function RelocateFileModal({
    isOpen,
    onClose,
    file,
    clientName,
    knowledgebaseClientId,
    onRelocate
}) {
    const [folders, setFolders] = useState([]);
    const [selectedFolderId, setSelectedFolderId] = useState('');
    const [newFilename, setNewFilename] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isRelocating, setIsRelocating] = useState(false);
    const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
    
    // Parse the current location to get current folder and filename
    const currentLocation = file?.sharepointLocation || '';
    // Extract filename from sharepointLocation (includes extension) as primary source
    const filenameFromLocation = currentLocation ? currentLocation.substring(currentLocation.lastIndexOf('/') + 1) : '';
    const currentFilename = filenameFromLocation || file?.acceptedName || file?.newName || file?.file?.name || '';
    const currentFolder = currentLocation ? currentLocation.substring(0, currentLocation.lastIndexOf('/')) : '';
    
    // Extract the client root folder (e.g., "Client Documents/Clients R - Z/Test, Me") from the path
    // This is used to skip searching for the client folder when loading folders
    const getClientRootFolder = () => {
        if (!currentLocation) return '';
        // Pattern: "Client Documents/Clients X - Y/ClientName/..." - we want first 3 segments
        const parts = currentLocation.split('/');
        if (parts.length >= 3) {
            return parts.slice(0, 3).join('/');
        }
        return currentFolder; // fallback to current folder
    };
    const clientRootFolder = getClientRootFolder();

    // Load folders when modal opens
    useEffect(() => {
        if (isOpen && clientName) {
            loadFolders();
            // Initialize filename with current filename
            setNewFilename(currentFilename);
        }
    }, [isOpen, clientName, currentFilename]);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setError('');
            setIsRelocating(false);
            setShowCreateFolderModal(false);
            setSelectedFolderId(''); // Reset so auto-select works for new file
        }
    }, [isOpen]);

    // Auto-select current folder when folders are loaded (only on initial load)
    useEffect(() => {
        if (folders.length > 0 && !selectedFolderId) {
            // Try to find the current folder
            const matchingFolder = folders.find(folder => {
                const folderPath = folder.full_path || folder.path || '';
                return folderPath === currentFolder || 
                       folderPath.endsWith(currentFolder) ||
                       currentFolder.endsWith(folderPath);
            });
            
            if (matchingFolder) {
                setSelectedFolderId(matchingFolder.id);
            } else {
                // Default to first folder if current not found
                setSelectedFolderId(folders[0].id);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [folders]);

    const loadFolders = async () => {
        if (!clientName) return;

        setLoading(true);
        setError('');

        try {
            // Load both existing folders and predefined folder options in parallel
            const [existingResponse, predefinedResponse] = await Promise.all([
                sharepointService.getClientFolders(clientName, clientRootFolder),
                sharepointService.getBackfillFolderOptions()
            ]);
            
            const rawExistingFolders = existingResponse.folders || [];
            const predefinedFolders = predefinedResponse.folders || [];
            
            // The clientBasePath is like "Client Documents/Clients R - Z/Test, Me"
            const clientBasePath = clientRootFolder || `Client Documents/${clientName.trim()}`;
            
            // Filter out nested duplicate folders from existing folders
            // Some folders have incorrect paths where the client path is duplicated
            // e.g., "Client Documents/Clients R - Z/Test, Me/Client Documents/Clients R - Z/Test, Me/FP Docs"
            // We want to keep only folders where full_path starts with clientBasePath but doesn't contain it again
            const existingFolders = rawExistingFolders.filter(f => {
                const fullPath = f.full_path || '';
                // Check if the client path appears more than once (nested duplicate)
                if (fullPath.startsWith(clientBasePath)) {
                    const restOfPath = fullPath.substring(clientBasePath.length);
                    // If the rest of the path contains the client path again, it's a duplicate
                    if (restOfPath.includes(clientBasePath) || restOfPath.includes('/Client Documents/')) {
                        return false;
                    }
                }
                return true;
            });
            
            // Compute the relative path of an existing folder (strip client base prefix)
            const getRelativePath = (f) => {
                const fullPath = f.full_path || f.path || f.name || '';
                if (fullPath.startsWith(clientBasePath + '/')) {
                    return fullPath.substring(clientBasePath.length + 1);
                } else if (fullPath === clientBasePath) {
                    return '/';
                }
                return fullPath;
            };

            // Build the full list of predefined folders (always shown first)
            // These use computed full_path so the backend can locate/create them
            const predefinedSection = predefinedFolders
                .filter(pf => pf.path !== '/' && pf.id !== 'root')
                .map(pf => ({
                    id: `predefined_${pf.id}`,
                    name: pf.name,
                    full_path: `${clientBasePath}/${pf.path.trim()}`,
                    path: pf.path,
                    description: pf.description,
                    isPredefined: true
                }));

            // Build a set of predefined names for deduplication
            const predefinedNameSet = new Set(predefinedSection.map(pf => pf.name.toLowerCase()));

            // Existing folders whose names aren't already covered by a predefined entry
            const existingOtherFolders = existingFolders.filter(f => {
                const rel = getRelativePath(f);
                return !predefinedNameSet.has(rel.toLowerCase()) && !predefinedNameSet.has((f.name || '').toLowerCase());
            });

            existingOtherFolders.sort((a, b) => {
                if (a.id === 'root') return -1;
                if (b.id === 'root') return 1;
                return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
            });

            // Combine: predefined first, separator, then other existing
            const allFolders = [
                ...predefinedSection,
                ...(predefinedSection.length > 0 && existingOtherFolders.length > 0
                    ? [{ id: 'separator', name: '──────────────', isSeparator: true }]
                    : []),
                ...existingOtherFolders
            ];

            setFolders(allFolders);
        } catch (err) {
            console.error('Error loading folders:', err);
            setError('Failed to load folders. Please try again.');
            setFolders([]);
        } finally {
            setLoading(false);
        }
    };

    const handleRelocate = async () => {
        if (!file || !clientName || !selectedFolderId || !newFilename.trim()) {
            setError('Please select a folder and enter a filename.');
            return;
        }

        // Validate filename for knowledgebase compatibility
        const filenameValidation = validateFilename(newFilename);
        if (!filenameValidation.isValid) {
            setError(filenameValidation.error);
            return;
        }

        // Find the selected folder to get its full path
        const selectedFolder = folders.find(f => f.id === selectedFolderId);
        if (!selectedFolder) {
            setError('Selected folder not found. Please try again.');
            return;
        }

        const fullTargetPath = selectedFolder.full_path || selectedFolder.path || selectedFolder.name || '';
        const trimmedFilename = newFilename.trim();

        // Extract relative subfolder path (strip client root from full path)
        // The backend expects just the subfolder path like "FP Docs/General", not the full SharePoint path
        // clientRootFolder is like "Client Documents/Clients R - Z/Test, Me"
        // fullTargetPath is like "Client Documents/Clients R - Z/Test, Me/FP Docs/General"
        // We need to extract just "FP Docs/General"
        let relativeSubfolderPath = fullTargetPath;
        if (clientRootFolder && fullTargetPath.startsWith(clientRootFolder)) {
            relativeSubfolderPath = fullTargetPath.substring(clientRootFolder.length);
            // Remove leading slash if present
            if (relativeSubfolderPath.startsWith('/')) {
                relativeSubfolderPath = relativeSubfolderPath.substring(1);
            }
        }

        // Check if anything actually changed
        const filenameChanged = trimmedFilename !== currentFilename;
        const folderChanged = fullTargetPath !== currentFolder;

        if (!filenameChanged && !folderChanged) {
            setError('No changes detected. Please modify the filename or select a different folder.');
            return;
        }

        setIsRelocating(true);
        setError('');

        try {
            const response = await sharepointService.relocateFile({
                fileId: file.fileId,
                clientName: clientName,
                currentPath: currentLocation,
                newName: trimmedFilename,
                targetFolder: relativeSubfolderPath,  // Send relative subfolder path, not full path
                knowledgebaseClientId: knowledgebaseClientId
            });

            if (response.success) {
                // Call the parent callback with success info
                if (onRelocate) {
                    onRelocate({
                        success: true,
                        file: file,
                        newPath: response.newPath,
                        sharepointUrl: response.sharepointUrl,
                        knowledgebaseStatus: response.knowledgebaseStatus,
                        newFilename: trimmedFilename,
                        newFolder: fullTargetPath
                    });
                }
                onClose();
            } else {
                setError(response.message || response.error || 'Failed to relocate file');
            }
        } catch (err) {
            console.error('Error relocating file:', err);

            let errorMessage = 'Failed to relocate file';
            if (err.response?.data) {
                const errorData = err.response.data;
                if (typeof errorData === 'string') {
                    errorMessage = errorData;
                } else if (errorData.detail) {
                    if (Array.isArray(errorData.detail)) {
                        const validationErrors = errorData.detail.map(error => {
                            if (typeof error === 'object' && error.msg) {
                                return `${error.loc?.join('.')} - ${error.msg}`;
                            }
                            return error.toString();
                        });
                        errorMessage = `Validation error: ${validationErrors.join(', ')}`;
                    } else {
                        errorMessage = errorData.detail;
                    }
                } else if (errorData.message) {
                    errorMessage = errorData.message;
                } else if (errorData.error) {
                    errorMessage = errorData.error;
                }
            } else if (err.message) {
                errorMessage = err.message;
            }

            setError(errorMessage);
        } finally {
            setIsRelocating(false);
        }
    };

    const handleCreateFolder = () => {
        setShowCreateFolderModal(true);
    };

    const handleFolderCreated = async (newFolder) => {
        // Refresh the folders list to include the new folder
        await loadFolders();

        // Auto-select the newly created folder
        if (newFolder && newFolder.id) {
            setSelectedFolderId(newFolder.id);
        }

        setShowCreateFolderModal(false);
    };

    const getParentFolderPath = () => {
        const selectedFolder = folders.find(f => f.id === selectedFolderId);
        return selectedFolder ? selectedFolder.full_path : `clients/${clientName}`;
    };

    // Check if file can be relocated (must have a SharePoint location)
    const canRelocate = file?.sharepointLocation && file?.fileId;

    if (!isOpen) return null;

    return (
        <>
            <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
                <div className="modal-dialog modal-lg" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                <i className="bi bi-arrow-left-right me-2 text-primary"></i>
                                Rename / Move File
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                                disabled={isRelocating}
                            ></button>
                        </div>

                        <div className="modal-body">
                            {!canRelocate ? (
                                <div className="alert alert-warning">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    This file cannot be relocated. It must first be pushed to SharePoint.
                                </div>
                            ) : (
                                <>
                                    <div className="alert alert-info">
                                        <i className="bi bi-info-circle me-2"></i>
                                        <strong>This will rename and/or move the file in SharePoint and update the knowledgebase.</strong>
                                        <br />
                                        <small className="text-muted">
                                            The old file will be removed from the knowledgebase and replaced with the new location.
                                        </small>
                                    </div>

                                    {/* Current Location */}
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Current Location:</label>
                                        <div className="bg-light border rounded p-2">
                                            <code>{currentLocation || 'Unknown'}</code>
                                        </div>
                                    </div>

                                    {error && (
                                        <div className="alert alert-danger">
                                            <i className="bi bi-exclamation-triangle me-2"></i>
                                            {error}
                                        </div>
                                    )}

                                    {/* New Filename */}
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">
                                            <i className="bi bi-file-earmark me-1"></i>
                                            New Filename:
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={newFilename}
                                            onChange={(e) => setNewFilename(e.target.value)}
                                            placeholder="Enter new filename..."
                                            disabled={isRelocating}
                                        />
                                        {newFilename !== currentFilename && (
                                            <small className="text-success">
                                                <i className="bi bi-check-circle me-1"></i>
                                                Filename will be changed
                                            </small>
                                        )}
                                    </div>

                                    {/* Target Folder Selection */}
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">
                                            <i className="bi bi-folder me-1"></i>
                                            Target Folder:
                                        </label>

                                        {loading ? (
                                            <div className="d-flex align-items-center">
                                                <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                                                Loading folders...
                                            </div>
                                        ) : folders.length > 0 ? (
                                            <>
                                                <div className="d-flex align-items-center mb-2">
                                                    <select
                                                        className="form-select me-2"
                                                        value={selectedFolderId}
                                                        onChange={(e) => setSelectedFolderId(e.target.value)}
                                                        disabled={isRelocating}
                                                    >
                                                        {folders.map((folder) => (
                                                            folder.isSeparator
                                                                ? <option key={folder.id} value={folder.id} disabled>──────────────</option>
                                                                : <option key={folder.id} value={folder.id}>{folder.name}</option>
                                                        ))}
                                                    </select>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary"
                                                        onClick={handleCreateFolder}
                                                        disabled={isRelocating}
                                                        title="Create new folder"
                                                    >
                                                        <i className="bi bi-folder-plus"></i>
                                                    </button>
                                                </div>
                                                {(() => {
                                                    const selectedFolder = folders.find(f => f.id === selectedFolderId);
                                                    const selectedPath = selectedFolder?.full_path || selectedFolder?.path || '';
                                                    if (selectedPath !== currentFolder) {
                                                        return (
                                                            <small className="text-success">
                                                                <i className="bi bi-check-circle me-1"></i>
                                                                File will be moved to a different folder
                                                            </small>
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                            </>
                                        ) : (
                                            <div className="alert alert-warning">
                                                <i className="bi bi-exclamation-triangle me-2"></i>
                                                No folders found for this client.
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-primary ms-2"
                                                    onClick={handleCreateFolder}
                                                    disabled={isRelocating}
                                                >
                                                    Create Folder
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Preview of new location */}
                                    {selectedFolderId && newFilename && (
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">New Location Preview:</label>
                                            <div className="bg-success bg-opacity-10 border border-success rounded p-2">
                                                <code className="text-success">
                                                    {(() => {
                                                        const selectedFolder = folders.find(f => f.id === selectedFolderId);
                                                        const folderPath = selectedFolder?.full_path || selectedFolder?.path || selectedFolder?.name || '';
                                                        return `${folderPath}/${newFilename.trim()}`;
                                                    })()}
                                                </code>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onClose}
                                disabled={isRelocating}
                            >
                                Cancel
                            </button>
                            {canRelocate && (
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleRelocate}
                                    disabled={isRelocating || loading || !newFilename.trim() || !selectedFolderId}
                                >
                                    {isRelocating ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Relocating...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-arrow-left-right me-2"></i>
                                            Relocate File
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {isOpen && <div className="modal-backdrop fade show"></div>}

            {/* Create Folder Modal */}
            <CreateFolderModal
                isOpen={showCreateFolderModal}
                onClose={() => setShowCreateFolderModal(false)}
                clientName={clientName}
                parentFolderPath={getParentFolderPath()}
                onFolderCreated={handleFolderCreated}
            />
        </>
    );
}
